import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { EnvelopeSimple } from '@phosphor-icons/react'

interface EmailPerformance {
  total_sent: number
  total_delivered: number
  total_opened: number
  total_clicked: number
  total_replied: number
  total_positive_replied: number
  total_bounced: number
  avg_open_rate: number
  avg_click_rate: number
  avg_reply_rate: number
}

interface Props {
  ep: EmailPerformance
}

const monoFont: React.CSSProperties = {
  fontFamily: "'Berkeley Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  fontWeight: 400,
}

// Static funnel geometry — purely decorative narrowing. Side bars carry data.
const SEGMENT_SHAPE = [
  { top: 1.0, bot: 0.86 }, // Sent
  { top: 0.86, bot: 0.72 }, // Delivered
  { top: 0.72, bot: 0.58 }, // Opened
  { top: 0.58, bot: 0.46 }, // Clicked
  { top: 0.46, bot: 0.36 }, // Replied
]

const TRAP_W = 400
const TRAP_H = 100
const TRAP_R = 14
const ROW_H = 48

/**
 * Trapezoid path with smoothly-rounded corners. Quadratic bezier at each
 * corner keeps the curve tangent to the slanted edge, avoiding the kink
 * that circular arcs produce on non-orthogonal corners.
 */
function trapPath(insetTopPct: number, insetBotPct: number, W: number, H: number, r: number) {
  const t = (insetTopPct / 100) * W
  const b = (insetBotPct / 100) * W

  const TL = { x: t, y: 0 }
  const TR = { x: W - t, y: 0 }
  const BR = { x: W - b, y: H }
  const BL = { x: b, y: H }

  const unit = (a: { x: number; y: number }, c: { x: number; y: number }) => {
    const dx = c.x - a.x
    const dy = c.y - a.y
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    return { x: dx / len, y: dy / len }
  }

  const cornerOffsets = (
    prev: { x: number; y: number },
    C: { x: number; y: number },
    next: { x: number; y: number },
  ) => {
    const inDir = unit(prev, C)
    const outDir = unit(C, next)
    return {
      pIn: { x: C.x - r * inDir.x, y: C.y - r * inDir.y },
      pOut: { x: C.x + r * outDir.x, y: C.y + r * outDir.y },
    }
  }

  const cTL = cornerOffsets(BL, TL, TR)
  const cTR = cornerOffsets(TL, TR, BR)
  const cBR = cornerOffsets(TR, BR, BL)
  const cBL = cornerOffsets(BR, BL, TL)

  return [
    `M ${cTL.pOut.x.toFixed(2)} ${cTL.pOut.y.toFixed(2)}`,
    `L ${cTR.pIn.x.toFixed(2)} ${cTR.pIn.y.toFixed(2)}`,
    `Q ${TR.x.toFixed(2)} ${TR.y.toFixed(2)} ${cTR.pOut.x.toFixed(2)} ${cTR.pOut.y.toFixed(2)}`,
    `L ${cBR.pIn.x.toFixed(2)} ${cBR.pIn.y.toFixed(2)}`,
    `Q ${BR.x.toFixed(2)} ${BR.y.toFixed(2)} ${cBR.pOut.x.toFixed(2)} ${cBR.pOut.y.toFixed(2)}`,
    `L ${cBL.pIn.x.toFixed(2)} ${cBL.pIn.y.toFixed(2)}`,
    `Q ${BL.x.toFixed(2)} ${BL.y.toFixed(2)} ${cBL.pOut.x.toFixed(2)} ${cBL.pOut.y.toFixed(2)}`,
    `L ${cTL.pIn.x.toFixed(2)} ${cTL.pIn.y.toFixed(2)}`,
    `Q ${TL.x.toFixed(2)} ${TL.y.toFixed(2)} ${cTL.pOut.x.toFixed(2)} ${cTL.pOut.y.toFixed(2)}`,
    'Z',
  ].join(' ')
}

const fmt = (n: number | undefined | null) => (n ?? 0).toLocaleString()

export const EmailPerformanceFunnel: React.FC<Props> = ({ ep }) => {
  const stages = useMemo(
    () => [
      { label: 'Sent', value: ep.total_sent ?? 0 },
      { label: 'Delivered', value: ep.total_delivered ?? 0 },
      { label: 'Opened', value: ep.total_opened ?? 0 },
      { label: 'Replied', value: ep.total_replied ?? 0 },
      { label: 'Positive Reply', value: ep.total_positive_replied ?? 0 },
    ],
    [ep],
  )

  const sent = Math.max(ep.total_sent, 1)
  const conversion = (ep.total_replied / sent) * 100
  const bounceRate = (ep.total_bounced / sent) * 100

  // No emails sent yet — a funnel of all-zeros is meaningless and previously
  // rendered a misleading full-width "Sent" baseline bar. Show an empty state.
  const hasData = (ep.total_sent ?? 0) > 0
  if (!hasData) {
    return (
      <div className="flex flex-col gap-5">
        <div className="min-w-0">
          <h2
            className="text-[var(--ed-text-1)]"
            style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
          >
            Email Performance
          </h2>
          <span
            className="block text-[var(--ed-text-4)] mt-1.5"
            style={{ ...monoFont, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            All time · lifetime totals
          </span>
        </div>
        <div className="flex flex-col items-center justify-center text-center gap-3 py-10">
          <div
            className="flex items-center justify-center rounded-full"
            style={{ width: 44, height: 44, background: 'var(--ed-bg-idle)', border: '1px solid var(--ed-border)' }}
          >
            <EnvelopeSimple className="h-5 w-5 text-[var(--ed-text-4)]" weight="regular" />
          </div>
          <p
            className="text-[var(--ed-text-1)]"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            No email activity yet
          </p>
          <p
            className="text-[var(--ed-text-4)]"
            style={{ fontSize: '12px', letterSpacing: '-0.1px', lineHeight: 1.5 }}
          >
            Launch a campaign to see performance here.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Header — title + callouts */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h2
            className="text-[var(--ed-text-1)]"
            style={{ fontSize: '15px', fontWeight: 510, letterSpacing: '-0.182px' }}
          >
            Email Performance
          </h2>
          <span
            className="block text-[var(--ed-text-4)] mt-1.5"
            style={{ ...monoFont, fontSize: '10px', letterSpacing: '0.18em', textTransform: 'uppercase' }}
          >
            All time · lifetime totals
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Callout
            value={`${conversion.toFixed(1)}`}
            unit="%"
            title="Conversion rate"
            sub="Sent → Replied"
          />
          <Callout
            value={fmt(ep.total_bounced)}
            unit={` · ${bounceRate.toFixed(1)}%`}
            title="Bounced"
            sub="Failure path"
            danger
          />
        </div>
      </div>

      {/* Funnel grid */}
      <div
        className="grid gap-5 items-stretch"
        style={{ gridTemplateColumns: 'minmax(160px, 200px) 1fr' }}
      >
        {/* Static trapezoid stack */}
        <div className="flex flex-col gap-2">
          {stages.map((s, i) => {
            const { top, bot } = SEGMENT_SHAPE[i]
            const insetTop = (1 - top) * 50
            const insetBot = (1 - bot) * 50
            const op = 0.9 - (i / (stages.length - 1)) * 0.2
            const d = trapPath(insetTop, insetBot, TRAP_W, TRAP_H, TRAP_R)
            return (
              <div key={s.label} className="relative" style={{ height: ROW_H }}>
                <svg
                  viewBox={`0 0 ${TRAP_W} ${TRAP_H}`}
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full overflow-visible block"
                  aria-hidden="true"
                >
                  <path d={d} fill="var(--ed-accent)" opacity={op.toFixed(2)} />
                </svg>
                <div
                  className="relative flex items-center justify-center h-full px-2 text-white"
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    letterSpacing: '-0.2px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pointerEvents: 'none',
                  }}
                >
                  {s.label}
                </div>
              </div>
            )
          })}
        </div>

        {/* Dynamic side rows */}
        <div className="flex flex-col gap-1.5">
          {stages.map((s, i) => {
            let barW: number
            let caption: React.ReactNode
            if (i === 0) {
              barW = 100
              caption = 'Baseline'
            } else {
              // Every stage is measured against Sent (stages[0]) — not the
              // immediately-previous stage. Each metric is a subset of Sent, so
              // the rate is always ≤100%, and the funnel reads as "share of all
              // mail that reached this stage" rather than stage-to-stage drop.
              const base = stages[0].value
              const rate = base > 0 ? (s.value / base) * 100 : 0
              barW = Math.max(2, Math.min(100, rate))
              caption = (
                <>
                  <span className="text-[var(--ed-text-2)]" style={{ fontWeight: 510 }}>
                    {rate.toFixed(1)}%
                  </span>{' '}
                  of Sent
                </>
              )
            }
            return (
              <div
                key={s.label}
                className="grid items-center gap-3.5 px-1"
                style={{ gridTemplateColumns: '1fr 120px', height: ROW_H }}
              >
                <div
                  className="relative h-5 overflow-hidden"
                  style={{ background: 'var(--ed-bg-track)', borderRadius: '6px' }}
                >
                  <motion.div
                    className="h-full"
                    style={{ background: 'var(--ed-accent)', opacity: 0.85, borderRadius: '6px' }}
                    initial={{ width: '0%' }}
                    animate={{ width: `${barW}%` }}
                    transition={{
                      duration: 0.9,
                      ease: [0.16, 1, 0.3, 1],
                      delay: 0.15 + i * 0.05,
                    }}
                  />
                </div>
                <div className="flex flex-col items-end justify-center gap-1.5">
                  <span
                    className="text-[var(--ed-text-1)] leading-none"
                    style={{
                      fontSize: '15px',
                      fontWeight: 510,
                      letterSpacing: '-0.13px',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {fmt(s.value)}
                  </span>
                  <span
                    className="text-[var(--ed-text-4)] leading-none text-right"
                    style={{
                      ...monoFont,
                      fontSize: '10px',
                      letterSpacing: '0.04em',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {caption}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

const Callout: React.FC<{
  value: string
  unit?: string
  title: string
  sub: string
  danger?: boolean
}> = ({ value, unit, title, sub, danger = false }) => {
  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2 rounded-md min-w-0"
      style={{
        background: danger ? 'var(--ed-danger-soft)' : 'var(--ed-bg-idle)',
        border: `1px solid ${danger ? 'var(--ed-danger-border)' : 'var(--ed-border)'}`,
      }}
    >
      <span
        className="leading-none whitespace-nowrap"
        style={{
          fontSize: '18px',
          fontWeight: 510,
          letterSpacing: '-0.3px',
          fontVariantNumeric: 'tabular-nums',
          color: danger ? 'var(--ed-danger)' : 'var(--ed-accent)',
        }}
      >
        {value}
        {unit && (
          <span
            style={{
              ...monoFont,
              fontSize: '11px',
              fontWeight: 510,
              color: 'var(--ed-text-3)',
              letterSpacing: 0,
              textTransform: 'none',
              marginLeft: '2px',
            }}
          >
            {unit}
          </span>
        )}
      </span>
      <div className="flex flex-col gap-0.5">
        <span
          className="text-[var(--ed-text-1)] whitespace-nowrap"
          style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.13px' }}
        >
          {title}
        </span>
        <span
          className="text-[var(--ed-text-4)] whitespace-nowrap"
          style={{
            ...monoFont,
            fontSize: '9px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
          }}
        >
          {sub}
        </span>
      </div>
    </div>
  )
}

export default EmailPerformanceFunnel
